/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  from,
  InMemoryCache,
  Operation,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { useAuthContext } from '@authentication/Authentication';
import { WindowWithRegisterNetworkRecovery } from '@hooks/NetworkStatusProvider';
import { Kind, OperationTypeNode } from 'graphql';
import { createClient } from 'graphql-ws';
import posthog from 'posthog-js';
import { PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import { KeyFieldPolicies } from './keyFieldPolicies';
import { QueryPolicies } from './queryPolicies';

const isNetworkError = (error: { message?: string; code?: string }) => {
  // Handle both direct network errors and wrapped errors
  const message = error?.message || '';
  return (
    message.includes('ERR_NETWORK_CHANGED') ||
    message.includes('ERR_INTERNET_DISCONNECTED') ||
    message.includes('Failed to fetch') ||
    message.includes('NetworkError') ||
    error?.code === 'NETWORK_ERROR'
  );
};

const isRetriableError = (error: { statusCode?: number; message?: string; code?: string }) => {
  if (isNetworkError(error)) return true;
  // When called from errorLink, error IS the networkError itself
  if (error?.statusCode >= 500) return true;
  if (error?.statusCode === 429) return true; // Rate limiting
  return false;
};

export const Apollo = ({ children }: PropsWithChildren) => {
  const { token, authenticated } = useAuthContext();
  const clientRef = useRef<ApolloClient<unknown>>();

  // Network recovery functionality
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      (window as WindowWithRegisterNetworkRecovery).__registerNetworkRecovery
    ) {
      const unregister = (window as WindowWithRegisterNetworkRecovery).__registerNetworkRecovery(
        async () => {
          if (clientRef.current) {
            try {
              clientRef.current.stop();
              await clientRef.current.resetStore();
            } catch (err) {
              console.error('Apollo: Error during network recovery:', err);
              try {
                await clientRef.current.reFetchObservableQueries(false);
              } catch (refetchErr) {
                console.error('Apollo: Failed to refetch queries:', refetchErr);
              }
            }
          }
        },
      );

      return unregister;
    }
    return () => {};
  }, []);

  const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      posthog.capture('graphql_error', { graphQLErrors, operationName: operation.operationName });
      // Apollo synthesizes graphQLErrors from networkError.result.errors when
      // the gateway returns a JSON error body. On a 504 the mutation is still
      // running server-side, and forwarding here would race against it. Skip
      // the forward only for that specific case; preserve existing behavior
      // for genuine GraphQL errors.
      const statusCode = (networkError as { statusCode?: number } | null)?.statusCode;
      if (statusCode === 504) {
        return null;
      }
      return forward(operation);
    }
    if (networkError) {
      console.error('Apollo: Network error detected:', networkError);
      posthog.capture('graphql_network_error', {
        networkError: networkError.message,
        operation: operation.operationName,
        isRetriable: isRetriableError(networkError),
      });

      // Don't retry if it's not a retriable error
      if (!isRetriableError(networkError)) {
        return null;
      }
    }
    return null;
  });

  const httpLinkUri =
    process.env.GATSBY_IS_LOCAL === 'true'
      ? process.env.GATSBY_GRAPHQL_DEV_URL
      : process.env.GATSBY_GRAPHQL_URL;
  const httpLink = createHttpLink({
    uri: httpLinkUri,
  });

  const authLink = useMemo(
    () =>
      setContext((_, { headers }: { headers: Record<string, unknown> }) => {
        if (!authenticated || !token) {
          return { headers };
        }
        try {
          return {
            headers: {
              ...headers,
              Authorization: `Bearer ${token}`,
            },
          };
        } catch {
          return { headers };
        }
      }),
    [token, authenticated],
  );

  // Enhanced WebSocket link with reconnection
  const wsLink = useMemo(() => {
    if (typeof window === 'undefined') return null;

    const wsImpl = typeof WebSocket !== 'undefined' ? WebSocket : null;
    const wsUrl = process.env.GATSBY_CORE_WEBSOCKET_URL;

    // Skip WebSocket link if URL is not configured (prevents 'undefined/graphql' connections)
    if (!wsUrl) return null;

    return new GraphQLWsLink(
      createClient({
        url: `${wsUrl}/graphql`,
        webSocketImpl: wsImpl,
        connectionParams: () => ({
          authorization: token ? `Bearer ${token}` : '',
        }),
        shouldRetry: (closedEvent: CloseEvent) => {
          // Retry on network errors but not authentication errors
          return closedEvent?.code !== 4401; // Don't retry on auth failures
        },
        retryAttempts: 5,
        retryWait: async (retries) => {
          // Exponential backoff: 1s, 2s, 4s, 8s, 16s
          await new Promise((resolve) => {
            setTimeout(resolve, Math.min(1000 * 2 ** retries, 30000));
          });
        },
      }),
    );
  }, [token]);

  // Main Apollo link chain
  const link = useMemo(() => {
    const retryLink = new RetryLink({
      delay: {
        initial: 1000,
        max: 30000,
        jitter: true,
      },
      attempts: {
        max: 5,
        retryIf: (error, operation) => {
          // editVolumeData runs a server-side multipart copy that can outlast
          // the 30s gateway timeout. A retry would race the still-running
          // first attempt and fail with "Failed to copy S3 object".
          if (operation.operationName === 'EditVolumeData') return false;
          return isRetriableError(error);
        },
      },
    });

    let baseLink;

    if (wsLink) {
      const splitLink = split(
        ({ query }: Operation) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === Kind.OPERATION_DEFINITION &&
            definition.operation === OperationTypeNode.SUBSCRIPTION
          );
        },
        wsLink,
        httpLink,
      );

      baseLink = from([authLink, retryLink, errorLink, splitLink]);
    } else {
      baseLink = from([authLink, retryLink, errorLink, httpLink]);
    }

    return baseLink;
  }, [authLink, errorLink, httpLink, wsLink]);

  const client = useMemo(() => {
    const apolloClient = new ApolloClient({
      link,
      defaultOptions: {
        query: {
          fetchPolicy: 'cache-first',
          errorPolicy: 'all', // Return partial data on network errors
        },
        watchQuery: {
          fetchPolicy: 'cache-first',
          errorPolicy: 'all',
        },
      },
      cache: new InMemoryCache({
        typePolicies: {
          ...KeyFieldPolicies,
          Query: {
            fields: {
              ...QueryPolicies,
            },
          },
        },
      }),
      connectToDevTools: process.env.GATSBY_ENV === 'dev',
    });

    // Store client reference for network recovery
    clientRef.current = apolloClient;

    return apolloClient;
  }, [link]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
